/**
 * Feb 2026 · Project Dashboard "Plus" — comprehensive metrics + graphs
 * (composes onto the existing AI-summary panel).
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip as ReTooltip,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend, LineChart, Line,
} from "recharts";
import {
  CheckSquare, Users, Rocket, Snail, ClipboardList, MessageSquare, CalendarClock, Ticket, Milestone,
  Bell, Loader2,
} from "lucide-react";
import { fmtRelative } from "@/lib/format";

const STATUS_COLORS = {
  pending_approval: "#f59e0b",   // amber — awaiting approver
  todo:             "#94a3b8",
  open:             "#94a3b8",
  in_progress:      "#0ea5e9",
  review:           "#8b5cf6",   // violet — approver-side review
  blocked:          "#f43f5e",
  done:             "#10b981",
  cancelled:        "#64748b",
};

export default function ProjectDashboardPlus({ projectId, project }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get(`/projects/${projectId}/dashboard-plus`)
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setData(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [projectId]);

  if (loading) return <div className="text-xs text-muted-foreground p-6">Loading dashboard metrics…</div>;
  if (!data) return <div className="text-xs text-muted-foreground p-6">Metrics unavailable.</div>;

  const t = data.totals || {};
  return (
    <div className="space-y-4" data-testid="project-dashboard-plus">
      {/* Totals strip */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        <Tile label="Open tasks" value={`${t.open_tasks}/${t.tasks}`} icon={CheckSquare} tone="sky" testid="dp-tile-tasks" />
        <Tile label="Open log items" value={t.open_log} icon={ClipboardList} tone="amber" testid="dp-tile-log" />
        <Tile label="Open tickets" value={t.open_tickets} icon={Ticket} tone="rose" testid="dp-tile-tickets" />
        <Tile label="Milestones" value={t.milestones} icon={Milestone} tone="violet" testid="dp-tile-milestones" />
        <Tile label="Active team (30d)" value={(data.most_active || []).length} icon={Users} tone="emerald" testid="dp-tile-team" />
      </div>

      {/* Two-column: status donut + creation vs closure line */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <ChartCard title="Task status distribution" testid="dp-chart-status">
          {data.task_status?.length ? (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={data.task_status} dataKey="count" nameKey="status" outerRadius={80} label={(e) => `${e.status} (${e.count})`}>
                  {data.task_status.map((s) => (
                    <Cell key={s.status} fill={STATUS_COLORS[s.status] || "#a1a1aa"} />
                  ))}
                </Pie>
                <ReTooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : <EmptyChart msg="No tasks yet" />}
        </ChartCard>

        <ChartCard title="Task creation vs closure · last 30 days" testid="dp-chart-trend">
          {data.task_trend_30d?.length ? (
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={data.task_trend_30d} margin={{ top: 8, right: 10, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="date" tick={{ fontSize: 9 }} tickFormatter={(d) => (d || "").slice(5)} interval={4} />
                <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                <ReTooltip />
                <Legend wrapperStyle={{ fontSize: 10 }} />
                <Line type="monotone" dataKey="created" stroke="#0ea5e9" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="closed" stroke="#10b981" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          ) : <EmptyChart msg="No task activity in 30 days" />}
        </ChartCard>
      </div>

      {/* Task allocation bar chart */}
      <ChartCard title="Open tasks per assignee (top 15)" testid="dp-chart-alloc">
        {data.task_allocation?.length ? (
          <ResponsiveContainer width="100%" height={Math.max(220, data.task_allocation.length * 22)}>
            <BarChart data={data.task_allocation} layout="vertical" margin={{ top: 4, right: 20, left: 40, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis type="number" tick={{ fontSize: 10 }} allowDecimals={false} />
              <YAxis type="category" dataKey="assignee_name" tick={{ fontSize: 10 }} width={110} />
              <ReTooltip />
              <Bar dataKey="open" fill="#0ea5e9" />
            </BarChart>
          </ResponsiveContainer>
        ) : <EmptyChart msg="No open tasks assigned" />}
      </ChartCard>

      {/* Active / inactive leaderboard */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <PeoplePanel title="Most active (30d)" icon={Rocket} tone="emerald" people={data.most_active} empty="No activity yet." testid="dp-people-active" />
        <PeoplePanel title="Least active (30d)" icon={Snail} tone="amber" people={data.least_active}
                      empty="Everyone's contributing." testid="dp-people-inactive"
                      nudgeButton={
                        <NudgeInactiveButton projectId={projectId} project={project} people={data.least_active} />
                      } />
      </div>

      {/* Milestones timeline + recent activity */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <MilestonesCard milestones={data.milestones} />
        <RecentUpdatesCard updates={data.recent_updates} projectId={projectId} />
        <RecentMeetingsCard meetings={data.recent_meetings} projectId={projectId} />
      </div>
    </div>
  );
}

function Tile({ label, value, icon: Icon, tone = "sky", testid }) {
  const toneCls = {
    sky:     "border-sky-400 text-sky-700",
    emerald: "border-emerald-400 text-emerald-700",
    amber:   "border-amber-400 text-amber-700",
    rose:    "border-rose-400 text-rose-700",
    violet:  "border-violet-400 text-violet-700",
  }[tone];
  return (
    <div className={`border ${toneCls} p-3 bg-background`} data-testid={testid}>
      <div className="label-eyebrow inline-flex items-center gap-1 text-[9px]"><Icon className="w-3 h-3" /> {label}</div>
      <div className="text-2xl font-bold mt-1 tabular-nums" style={{ fontFamily: "var(--font-heading)" }}>{value}</div>
    </div>
  );
}

function ChartCard({ title, testid, children }) {
  return (
    <div className="border border-border p-3" data-testid={testid}>
      <div className="label-eyebrow mb-2">{title}</div>
      {children}
    </div>
  );
}

function EmptyChart({ msg }) {
  return <div className="h-52 flex items-center justify-center text-xs text-muted-foreground italic">{msg}</div>;
}

function PeoplePanel({ title, icon: Icon, tone, people = [], empty, testid, nudgeButton }) {
  const toneCls = tone === "emerald" ? "border-emerald-400 bg-emerald-50/30" : "border-amber-400 bg-amber-50/30";
  return (
    <div className={`border ${toneCls} p-3`} data-testid={testid}>
      <div className="flex items-center gap-1.5 mb-2">
        <div className="label-eyebrow inline-flex items-center gap-1.5">
          <Icon className="w-3 h-3" /> {title}
        </div>
        <div className="ml-auto">{nudgeButton}</div>
      </div>
      {(!people || people.length === 0) ? (
        <div className="text-[11px] text-muted-foreground italic">{empty}</div>
      ) : (
        <ul className="space-y-1">
          {people.map((p, i) => (
            <li key={p.user_id || i} className="text-xs flex items-center gap-2" data-testid={`${testid}-row-${i}`}>
              <span className="w-4 text-right text-muted-foreground text-[10px]">#{i + 1}</span>
              <span className="flex-1 truncate font-semibold">{p.name}</span>
              <span className="text-[10px] text-muted-foreground">
                {p.tasks_touched} tasks · {p.hours}h · {p.updates_posted} upd
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function MilestonesCard({ milestones = [] }) {
  return (
    <div className="border border-border p-3" data-testid="dp-milestones">
      <div className="label-eyebrow inline-flex items-center gap-1.5 mb-2"><Milestone className="w-3 h-3" /> Milestones</div>
      {milestones.length === 0 ? (
        <div className="text-[11px] text-muted-foreground italic">No milestones set.</div>
      ) : (
        <ul className="space-y-1.5">
          {milestones.slice(0, 8).map((m) => {
            const done = m.status === "done" || m.status === "completed" || m.completed_at;
            return (
              <li key={m.id} className="text-xs flex items-center gap-2">
                <span className={`w-1.5 h-1.5 rounded-full ${done ? "bg-emerald-500" : "bg-amber-500"}`} />
                <span className="flex-1 truncate">{m.title}</span>
                <span className="text-[10px] text-muted-foreground">{m.due || "—"}</span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function RecentUpdatesCard({ updates = [], projectId }) {
  return (
    <div className="border border-border p-3" data-testid="dp-updates">
      <div className="label-eyebrow inline-flex items-center gap-1.5 mb-2"><MessageSquare className="w-3 h-3" /> Recent status updates</div>
      {updates.length === 0 ? (
        <div className="text-[11px] text-muted-foreground italic">No updates in the last 30 days.</div>
      ) : (
        <ul className="space-y-2">
          {updates.map((u) => (
            <li key={u.id} className="border-l-2 border-primary/40 pl-2 text-xs">
              <div className="text-[10px] text-muted-foreground">{u.author_name} · {fmtRelative(u.created_at)}</div>
              <div className="line-clamp-2">{u.preview}</div>
            </li>
          ))}
        </ul>
      )}
      <Link to={`/app/projects/${projectId}?tab=updates`} className="text-[10px] text-primary underline mt-2 inline-block">See all →</Link>
    </div>
  );
}

function RecentMeetingsCard({ meetings = [], projectId }) {
  return (
    <div className="border border-border p-3" data-testid="dp-meetings">
      <div className="label-eyebrow inline-flex items-center gap-1.5 mb-2"><CalendarClock className="w-3 h-3" /> Recent meetings</div>
      {meetings.length === 0 ? (
        <div className="text-[11px] text-muted-foreground italic">No meetings logged.</div>
      ) : (
        <ul className="space-y-1.5">
          {meetings.map((m) => (
            <li key={m.id} className="text-xs">
              <div className="font-semibold truncate">{m.title || "Meeting"}</div>
              <div className="text-[10px] text-muted-foreground">{(m.start_time || "").slice(0, 16).replace("T", " ")}</div>
            </li>
          ))}
        </ul>
      )}
      <Link to={`/app/projects/${projectId}?tab=meetings`} className="text-[10px] text-primary underline mt-2 inline-block">See all →</Link>
    </div>
  );
}

// ─── Feb 2026 · Nudge inactive members ─────────────────────────

function NudgeInactiveButton({ projectId, project, people = [] }) {
  const [open, setOpen] = useState(false);
  if (!people?.length) return null;
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="text-[10px] inline-flex items-center gap-1 px-2 py-0.5 border border-amber-500 text-amber-700 hover:bg-amber-50"
        data-testid="dp-nudge-btn"
      >
        <Bell className="w-3 h-3" /> Nudge
      </button>
      {open && (
        <NudgeDialog projectId={projectId} project={project} people={people} onClose={() => setOpen(false)} />
      )}
    </>
  );
}

function NudgeDialog({ projectId, project, people, onClose }) {
  const [selected, setSelected] = useState(new Set(people.map((p) => p.user_id)));
  const [channels, setChannels] = useState(new Set(["in_app"]));
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const toggle = (uid) => {
    const s = new Set(selected);
    if (s.has(uid)) s.delete(uid); else s.add(uid);
    setSelected(s);
  };
  const toggleChan = (c) => {
    const s = new Set(channels);
    if (s.has(c)) s.delete(c); else s.add(c);
    setChannels(s);
  };
  const submit = async () => {
    const user_ids = [...selected];
    if (!user_ids.length) { toast.error("Pick at least one person"); return; }
    if (!channels.size) { toast.error("Pick at least one channel"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/nudge-inactive`, {
        user_ids,
        channels: [...channels],
        message: message.trim() || null,
      });
      const skipped = data?.results?.skipped?.length || 0;
      toast.success(`Nudged ${data?.recipients || user_ids.length} member(s)${skipped ? ` · ${skipped} skipped (channel not configured)` : ""}`);
      onClose();
    } catch (e) { toast.error(apiErrorMessage(e, "Nudge failed")); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md rounded-none" data-testid="nudge-dialog">
        <DialogHeader>
          <DialogTitle className="inline-flex items-center gap-2">
            <Bell className="w-4 h-4 text-primary" /> Nudge inactive members
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="border border-border p-2 max-h-40 overflow-y-auto">
            {people.map((p) => (
              <label key={p.user_id} className="flex items-center gap-2 py-1 text-xs cursor-pointer">
                <input type="checkbox" checked={selected.has(p.user_id)} onChange={() => toggle(p.user_id)}
                        data-testid={`nudge-user-${p.user_id}`} />
                <span className="flex-1 truncate">{p.name}</span>
                <span className="text-[10px] text-muted-foreground">{p.tasks_touched}t · {p.hours}h</span>
              </label>
            ))}
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Channels</Label>
            <div className="flex gap-1.5 flex-wrap" data-testid="nudge-channels">
              {[["in_app", "In-app"], ["email", "Email"], ["whatsapp", "WhatsApp"]].map(([c, label]) => {
                const active = channels.has(c);
                return (
                  <button key={c} type="button" onClick={() => toggleChan(c)}
                           className={`px-2.5 py-1 text-[11px] border ${active ? "border-primary bg-primary text-primary-foreground" : "border-border bg-background hover:bg-muted"}`}
                           data-testid={`nudge-chan-${c}`}>
                    {label}
                  </button>
                );
              })}
            </div>
            <p className="text-[9px] text-muted-foreground italic mt-1">In-app is always safe. Email/WhatsApp gracefully fall back to in-app if not configured for this workspace.</p>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Custom message (optional)</Label>
            <Textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={3}
                       placeholder={`Hey — quick nudge on "${project?.name}". You've been quiet the last 30 days…`}
                       data-testid="nudge-message" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="rounded-none">Cancel</Button>
          <Button onClick={submit} disabled={busy || !selected.size || !channels.size} className="rounded-none" data-testid="nudge-send">
            {busy ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Sending…</> : `Send nudge to ${selected.size}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

